import React, { useEffect, useId, useMemo, useState } from "react";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from "../../../components/Modal/Modal";
import {
  createQuizzVideo,
  updateQuizzVideo,
} from "../../../services/quizzvideoServices";
import { useNotification } from "../../../components/Notification/NotificationManager";
import {
  FaSave,
  FaTimes,
  FaVideo,
  FaImage,
  FaFolderOpen,
  FaGavel,
  FaFileAlt,
  FaExternalLinkAlt,
} from "react-icons/fa";

function normalizeUrl(url) {
  if (!url) return "";
  const trimmed = String(url).trim();
  if (!trimmed) return "";
  return /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
}

function normalizeImageUrl(url) {
  if (!url) return "";
  const trimmed = String(url).trim();
  if (!trimmed) return "";

  if (/^https?:\/\//i.test(trimmed) || trimmed.startsWith("//")) {
    return trimmed;
  }

  if (trimmed.startsWith("/")) {
    return `${window.location.origin}${trimmed}`;
  }

  return `${window.location.origin}/${trimmed}`;
}

function FieldLabel({ icon, children, required = false }) {
  return (
    <label className="mb-2 flex items-center gap-2 text-sm font-semibold text-slate-800 dark:text-slate-100">
      <span className="text-slate-500 dark:text-slate-400">{icon}</span>
      <span>{children}</span>
      {required ? <span className="text-rose-500">*</span> : null}
    </label>
  );
}

function Input({
  value,
  onChange,
  placeholder,
  type = "text",
  autoFocus = false,
}) {
  return (
    <input
      data-autofocus={autoFocus ? true : undefined}
      type={type}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      className="h-11 w-full rounded-2xl border border-slate-300 bg-white px-4 text-sm text-slate-900 outline-none transition focus:border-indigo-500 focus:ring-4 focus:ring-indigo-500/10 dark:border-slate-700 dark:bg-slate-950 dark:text-white"
    />
  );
}

function Textarea({ value, onChange, placeholder, rows = 6 }) {
  return (
    <textarea
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      rows={rows}
      className="w-full rounded-2xl border border-slate-300 bg-white px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-indigo-500 focus:ring-4 focus:ring-indigo-500/10 dark:border-slate-700 dark:bg-slate-950 dark:text-white"
    />
  );
}

export default function QuizzArbitrageModal({
  isActive,
  onClose,
  refreshQuizz,
  mode = "create",
  quizzData = null,
}) {
  const { addNotification } = useNotification();

  const titleId = useId();
  const descriptionId = useId();

  const isEdit = mode === "edit";
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [formData, setFormData] = useState({
    nom_quizz: "",
    famille_quizz: "",
    lien_url_video: "",
    lien_url_image: "",
    decision: "",
    description: "",
  });

  useEffect(() => {
    if (isEdit && quizzData) {
      setFormData({
        nom_quizz: quizzData.nom_quizz || "",
        famille_quizz: quizzData.famille_quizz || "",
        lien_url_video: quizzData.lien_url_video || "",
        lien_url_image: quizzData.lien_url_image || "",
        decision: quizzData.decision || "",
        description: quizzData.description || "",
      });
    } else {
      setFormData({
        nom_quizz: "",
        famille_quizz: "",
        lien_url_video: "",
        lien_url_image: "",
        decision: "",
        description: "",
      });
    }
  }, [isEdit, quizzData, isActive]);

  const previewVideo = useMemo(
    () => normalizeUrl(formData.lien_url_video),
    [formData.lien_url_video],
  );

  const previewImage = useMemo(
    () => normalizeImageUrl(formData.lien_url_image),
    [formData.lien_url_image],
  );

  const handleChange = (field) => (e) => {
    setFormData((prev) => ({
      ...prev,
      [field]: e.target.value,
    }));
  };

  const handleClose = () => {
    if (isSubmitting) return;
    onClose?.();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;

    const payload = {
      nom_quizz: formData.nom_quizz.trim(),
      famille_quizz: formData.famille_quizz.trim(),
      lien_url_video: formData.lien_url_video.trim(),
      lien_url_image: formData.lien_url_image.trim(),
      decision: formData.decision.trim(),
      description: formData.description.trim() || null,
    };

    if (!payload.nom_quizz) {
      addNotification("Le nom du quizz est obligatoire.", "error");
      return;
    }

    if (!payload.famille_quizz) {
      addNotification("La famille du quizz est obligatoire.", "error");
      return;
    }

    if (!payload.lien_url_video) {
      addNotification("Le lien vidéo est obligatoire.", "error");
      return;
    }

    if (!payload.lien_url_image) {
      addNotification("Le lien image est obligatoire.", "error");
      return;
    }

    if (!payload.decision) {
      addNotification("La décision attendue est obligatoire.", "error");
      return;
    }

    try {
      setIsSubmitting(true);

      let result = null;

      if (isEdit && quizzData?.id) {
        result = await updateQuizzVideo({
          id: quizzData.id,
          ...payload,
        });
      } else {
        result = await createQuizzVideo(payload);
      }

      if (!result) {
        throw new Error("Réponse invalide du serveur");
      }

      addNotification(
        isEdit
          ? "Quizz vidéo modifié avec succès."
          : "Quizz vidéo créé avec succès.",
        "success",
      );

      await refreshQuizz?.();
      onClose?.();
    } catch (error) {
      console.error("Erreur lors de l'enregistrement du quizz vidéo :", error);
      addNotification(
        "Erreur lors de l'enregistrement du quizz vidéo.",
        "error",
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal
      isOpen={isActive}
      onClose={handleClose}
      size="3xl"
      labelledById={titleId}
      describedById={descriptionId}
    >
      <ModalHeader
        id={titleId}
        title={isEdit ? "Modifier le quizz vidéo" : "Créer un quizz vidéo"}
        subtitle={
          isEdit
            ? "Mettez à jour la vidéo, le visuel, la famille et la décision attendue."
            : "Ajoutez une nouvelle vidéo quizz pour les arbitres et commissaires sportifs."
        }
        onClose={handleClose}
      />

      <form onSubmit={handleSubmit} className="flex min-h-0 flex-1 flex-col">
        <ModalBody id={descriptionId}>
          <div className="space-y-6">
            <div className="rounded-3xl border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-800 dark:bg-slate-900">
              <div className="grid gap-5 lg:grid-cols-2">
                <div>
                  <FieldLabel icon={<FaVideo />} required>
                    Nom du quizz
                  </FieldLabel>
                  <Input
                    autoFocus
                    value={formData.nom_quizz}
                    onChange={handleChange("nom_quizz")}
                    placeholder="Ex. Shido ou non ?"
                  />
                </div>

                <div>
                  <FieldLabel icon={<FaFolderOpen />} required>
                    Famille
                  </FieldLabel>
                  <Input
                    value={formData.famille_quizz}
                    onChange={handleChange("famille_quizz")}
                    placeholder="Ex. Pénalités, Immobilisations, Soremade..."
                  />
                </div>
              </div>
            </div>

            <div className="grid gap-6 xl:grid-cols-[minmax(0,1fr),340px]">
              <div className="space-y-6">
                <div className="rounded-3xl border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-800 dark:bg-slate-900">
                  <FieldLabel icon={<FaVideo />} required>
                    Lien URL vidéo
                  </FieldLabel>
                  <Input
                    value={formData.lien_url_video}
                    onChange={handleChange("lien_url_video")}
                    placeholder="https://..."
                  />
                  <p className="mt-2 text-xs text-slate-500 dark:text-slate-400">
                    Lien vers la vidéo que l’arbitre devra analyser.
                  </p>

                  {previewVideo ? (
                    <div className="mt-3">
                      <a
                        href={previewVideo}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 rounded-2xl border border-indigo-200 bg-indigo-50 px-3 py-2 text-sm font-semibold text-indigo-700 transition hover:bg-indigo-100 dark:border-indigo-500/20 dark:bg-indigo-500/10 dark:text-indigo-300"
                      >
                        <FaExternalLinkAlt className="text-xs" />
                        Ouvrir la vidéo
                      </a>
                    </div>
                  ) : null}
                </div>

                <div className="rounded-3xl border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-800 dark:bg-slate-900">
                  <FieldLabel icon={<FaImage />} required>
                    Lien URL image
                  </FieldLabel>
                  <Input
                    value={formData.lien_url_image}
                    onChange={handleChange("lien_url_image")}
                    placeholder="https://... ou /uploads/..."
                  />
                  <p className="mt-2 text-xs text-slate-500 dark:text-slate-400">
                    Image miniature utilisée pour représenter le quizz.
                  </p>
                </div>

                <div className="rounded-3xl border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-800 dark:bg-slate-900">
                  <FieldLabel icon={<FaGavel />} required>
                    Décision attendue
                  </FieldLabel>
                  <Input
                    value={formData.decision}
                    onChange={handleChange("decision")}
                    placeholder="Ex. Waza-ari, Matte, Shido blanc..."
                  />
                </div>
              </div>

              <div className="rounded-3xl border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-800 dark:bg-slate-900">
                <div className="mb-3 flex items-center gap-2 text-sm font-semibold text-slate-800 dark:text-slate-100">
                  <FaImage className="text-slate-500 dark:text-slate-400" />
                  Aperçu visuel
                </div>

                {previewImage ? (
                  <div className="overflow-hidden rounded-2xl border border-slate-200 bg-slate-50 dark:border-slate-700 dark:bg-slate-800">
                    <img
                      src={previewImage}
                      alt={formData.nom_quizz || "Aperçu du quizz"}
                      className="h-56 w-full object-cover"
                      onError={(e) => {
                        e.currentTarget.style.display = "none";
                      }}
                    />
                  </div>
                ) : (
                  <div className="flex h-56 items-center justify-center rounded-2xl border border-dashed border-slate-300 bg-slate-50 text-slate-400 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-500">
                    Aucun visuel renseigné
                  </div>
                )}

                <div className="mt-4 rounded-2xl border border-slate-200 bg-slate-50 p-3 dark:border-slate-700 dark:bg-slate-800">
                  <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">
                    Aperçu rapide
                  </div>

                  <div className="mt-2 text-sm font-bold text-slate-900 dark:text-white">
                    {formData.nom_quizz.trim() || "Sans nom"}
                  </div>

                  <div className="mt-2 text-xs text-slate-500 dark:text-slate-400">
                    Famille : {formData.famille_quizz.trim() || "—"}
                  </div>

                  <div className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                    Décision : {formData.decision.trim() || "—"}
                  </div>
                </div>
              </div>
            </div>

            <div className="rounded-3xl border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-800 dark:bg-slate-900">
              <FieldLabel icon={<FaFileAlt />}>
                Description / correction
              </FieldLabel>
              <Textarea
                value={formData.description}
                onChange={handleChange("description")}
                rows={8}
                placeholder="Description optionnelle : explication de la situation, correction, commentaire..."
              />
            </div>
          </div>
        </ModalBody>

        <ModalFooter>
          <button
            type="button"
            onClick={handleClose}
            disabled={isSubmitting}
            className="inline-flex h-11 items-center justify-center gap-2 rounded-2xl border border-slate-300 bg-white px-4 text-sm font-semibold text-slate-700 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-200 dark:hover:bg-slate-800"
          >
            <FaTimes />
            Annuler
          </button>

          <button
            type="submit"
            disabled={isSubmitting}
            className="inline-flex h-11 items-center justify-center gap-2 rounded-2xl bg-indigo-600 px-5 text-sm font-semibold text-white transition hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-60"
          >
            <FaSave />
            {isSubmitting
              ? "Enregistrement..."
              : isEdit
                ? "Enregistrer les modifications"
                : "Créer le quizz"}
          </button>
        </ModalFooter>
      </form>
    </Modal>
  );
}
